import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import SidebarV2 from './SidebarV2';
import ChatWidget from '../chat/ChatWidget';
import SubscriptionBanner from '../subscription/SubscriptionBanner';
import CertificateExpiryBanner from '../sysadmin/CertificateExpiryBanner';
import DefaultPickupLocationPrompt from '../profile/DefaultPickupLocationPrompt';
import { PageErrorBoundary } from '@/components/common/PageErrorBoundary';
import { useActivityRefresh } from '@/hooks/useActivityRefresh';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/store/languageStore';
import { resolveViewTitle } from '@/utils/documentTitle';

const SIDEBAR_COLLAPSED_KEY = 'dqfleet.sidebarCollapsed';

const Layout: React.FC = () => {
  // Aktivera proaktiv token refresh baserat på användaraktivitet
  useActivityRefresh();
  const location = useLocation();
  const { t } = useTranslation();
  usePageTitle(resolveViewTitle(location.pathname, t));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (raw === '1') setSidebarCollapsed(true);
      else if (raw === '0') setSidebarCollapsed(false);
      else if (typeof window !== 'undefined' && window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return (
    <div className="layoutWrapper bg-bgLight flex h-screen gap-4 p-4 animate-fadeIn">
      <div
        className={`sidebar h-full shrink-0 bg-bgContainer rounded-lg flex flex-col overflow-hidden transition-[width] duration-200 ease-out ${
          sidebarCollapsed ? 'w-[4.5rem]' : 'w-[220px]'
        }`}
      >
        <SidebarV2 collapsed={sidebarCollapsed} onToggleCollapsed={toggleSidebarCollapsed} />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
        <header className="flex-shrink-0">
          <Header />
        </header>
        {/* Visa banner för demo/trial-användare */}
        <SubscriptionBanner />
        {/* Varning för systemadmin när bevakade certifikat snart går ut */}
        <CertificateExpiryBanner />
        <div className="relative min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable]">
          <PageErrorBoundary
            resetKey={location.key}
            title={t('common.pageErrorTitle')}
            description={t('common.pageErrorDescription')}
          >
            <Outlet />
          </PageErrorBoundary>
        </div>
      </div>
      <ChatWidget />
      {/* Be användaren välja förvald utlämningsplats vid första inloggning */}
      <DefaultPickupLocationPrompt />
    </div>
  );
};

export default Layout;
